import React, { useState, useRef, useCallback } from 'react';
import FeatherItem from './FeatherItem';
import GoldMask from './GoldMask';
import { useAudio } from '../hooks/useAudio';
import { Volume2, VolumeX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feather definitions with whispers - 5 speak, 1 silent (Shamrock Green)
const FEATHERS = [
  {
    id: 'scarlett-wing',
    name: 'Scarlett Wing',
    type: 'scarlett',
    color: '#c62828',
    whisper: 'She always wore red. Even at the end.',
    whisperAudio: '/audio/feather-scarlett-wing.mp3',
    whisperDelay: 1500,
  },
  {
    id: 'navy-black-longtail',
    name: 'Navy-Black Longtail',
    type: 'longtail',
    color: '#2c3e50',
    whisper: 'Happy here with you.',
    whisperAudio: '/audio/feather-navy-longtail.mp3',
    whisperDelay: 1500,
  },
  {
    id: 'camelot-golden-rust',
    name: 'Camelot Golden Rust',
    type: 'camelot',
    color: '#bf6c00',
    whisper: 'You were never ordinary, Eva.',
    whisperAudio: '/audio/feather-camelot-golden.mp3',
    whisperDelay: 2000,
  },
  {
    id: 'rainbow-hybrid',
    name: 'Rainbow Hybrid',
    type: 'rainbow',
    color: null,
    whisper: 'Seasons change. So must we.',
    whisperAudio: '/audio/feather-rainbow.mp3',
    whisperDelay: 1800,
  },
  {
    id: 'shamrock-green',
    name: 'Shamrock Green',
    type: 'shamrock',
    color: '#2e7d32',
    whisper: null,  // Silent feather
    whisperAudio: null,
    whisperDelay: null,
  },
  {
    id: 'yellow-sulphur',
    name: 'Yellow Sulphur\nCrested Top-Knot',
    type: 'topknot',
    color: '#f9a825',
    whisper: 'We have been doing this for generations.',
    whisperAudio: '/audio/feather-yellow-sulphur.mp3',
    whisperDelay: 1800,
  },
];

const MAX_FEATHERS = 100;

// Generate random angle for natural feather placement
function getRandomAngle() {
  return Math.floor(Math.random() * 360);
}

// Generate initial position on mask EDGE (not center)
function getEdgePosition(index) {
  // Distribute around the mask perimeter
  const positions = [
    { x: 40, y: 30 },   // Top left
    { x: 140, y: 15 },  // Top center
    { x: 240, y: 30 },  // Top right
    { x: 260, y: 90 },  // Right side
    { x: 240, y: 150 }, // Bottom right
    { x: 140, y: 165 }, // Bottom center
    { x: 40, y: 150 },  // Bottom left
    { x: 20, y: 90 },   // Left side
  ];
  const pos = positions[index % positions.length];
  // Add small randomness
  return {
    x: pos.x + (Math.random() - 0.5) * 20,
    y: pos.y + (Math.random() - 0.5) * 15,
  };
}

export default function WakeExperience() {
  // Each placed feather has: { instanceId, featherId, angle, offset }
  const [placedFeathers, setPlacedFeathers] = useState([]);
  const [endingPhase, setEndingPhase] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [whisperData, setWhisperData] = useState(null);
  const [audioStarted, setAudioStarted] = useState(false);

  const { startAmbient, playFeatherPlace, dipAmbient, restoreAmbient, setMuted } = useAudio();
  const whisperActiveRef = useRef(false);
  const instanceCounterRef = useRef(0);

  /* ── Audio bootstrap ── */
  const handleStartAudio = useCallback(() => {
    if (!audioStarted) {
      startAmbient();
      setAudioStarted(true);
    }
  }, [audioStarted, startAmbient]);

  /* ── TTS Voice Generation ── */
  const playVoice = useCallback(async (text) => {
    if (isMuted) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'nova' }),
      });
      
      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.volume = 0.8;
        audio.play().catch(() => {});
        
        // Cleanup URL after audio ends
        audio.onended = () => URL.revokeObjectURL(audioUrl);
      }
    } catch (err) {
      console.log('Voice generation unavailable:', err.message);
    }
  }, [isMuted]);

  /* ── Whisper handler with voice ── */
  const handleWhisperStart = useCallback((whisperText, whisperAudioSrc) => {
    if (whisperActiveRef.current || !whisperText) return;
    whisperActiveRef.current = true;

    dipAmbient();
    
    // Split text into words for animated display
    const words = whisperText.split(' ');
    setWhisperData({ words, visibleWords: [], phase: 'appearing', fullText: whisperText });

    // Play uploaded audio file (or fall back to TTS)
    if (whisperAudioSrc && !isMuted) {
      const audio = new Audio(whisperAudioSrc);
      audio.volume = 0.8;
      audio.play().catch(() => {});
    } else if (!isMuted) {
      playVoice(whisperText);
    }

    // Animate words appearing
    words.forEach((word, i) => {
      setTimeout(() => {
        setWhisperData(prev =>
          prev ? { ...prev, visibleWords: [...prev.visibleWords, word] } : null
        );
      }, i * 600 + 100);
    });

    const allAt = (words.length - 1) * 600 + 100 + 2800;

    setTimeout(() => {
      setWhisperData(prev => prev ? { ...prev, phase: 'fading' } : null);
      whisperActiveRef.current = false;
    }, allAt);

    setTimeout(() => {
      setWhisperData(null);
      restoreAmbient();
    }, allAt + 1600);
  }, [dipAmbient, restoreAmbient, playVoice, isMuted]);

  /* ── Place feather on mask ── */
  const handlePlaceFeather = useCallback((featherId) => {
    if (placedFeathers.length >= MAX_FEATHERS || endingPhase > 0) return;

    handleStartAudio();
    playFeatherPlace();

    const edgePos = getEdgePosition(placedFeathers.length);
    const newInstance = {
      instanceId: `${featherId}-${++instanceCounterRef.current}`,
      featherId,
      angle: getRandomAngle(),
      x: edgePos.x,
      y: edgePos.y,
    };

    setPlacedFeathers(prev => [...prev, newInstance]);
  }, [placedFeathers.length, endingPhase, handleStartAudio, playFeatherPlace]);

  /* ── Update feather position (drag) ── */
  const handleUpdateFeatherPosition = useCallback((instanceId, x, y) => {
    if (endingPhase > 0) return;
    setPlacedFeathers(prev => prev.map(f => 
      f.instanceId === instanceId ? { ...f, x, y } : f
    ));
  }, [endingPhase]);

  /* ── Update feather angle (rotation) ── */
  const handleUpdateFeatherAngle = useCallback((instanceId, angle) => {
    if (endingPhase > 0) return;
    setPlacedFeathers(prev => prev.map(f => 
      f.instanceId === instanceId ? { ...f, angle } : f
    ));
  }, [endingPhase]);

  /* ── Remove feather from mask ── */
  const handleRemoveFeather = useCallback((instanceId) => {
    if (endingPhase > 0) return;
    
    setPlacedFeathers(prev => prev.filter(f => f.instanceId !== instanceId));
    playFeatherPlace(); // Soft sound on removal too
  }, [endingPhase, playFeatherPlace]);

  /* ── Complete the mask (trigger ending) ── */
  const handleCompleteMask = useCallback(() => {
    if (placedFeathers.length === 0 || endingPhase > 0) return;
    
    handleStartAudio();
    setTimeout(() => setEndingPhase(1), 700);
    setTimeout(() => setEndingPhase(2), 5700);
    
    // Play final voice — use uploaded audio file
    const endAudio = new Audio('/audio/ending-look-what-you-made.mp3');
    endAudio.play().catch(() => {});
    setTimeout(() => {
      const readyAudio = new Audio('/audio/ending-im-ready.mp3');
      readyAudio.play().catch(() => {});
    }, 5000);

    // Phase 3: "I'm ready" text glows after audio finishes (~8s)
    setTimeout(() => setEndingPhase(3), 8500);
    // Phase 4: Gold floods the screen
    setTimeout(() => setEndingPhase(4), 10500);
  }, [placedFeathers.length, endingPhase, handleStartAudio, playVoice]);

  /* ── Mute toggle ── */
  const handleMuteToggle = useCallback(() => {
    handleStartAudio();
    setIsMuted(prev => {
      const next = !prev;
      setMuted(next);
      return next;
    });
  }, [handleStartAudio, setMuted]);

  const topFeathers = FEATHERS.slice(0, 3);
  const bottomFeathers = FEATHERS.slice(3, 6);

  // Convert placed instances to full feather objects with placement data
  const placedObjects = placedFeathers.map(instance => {
    const feather = FEATHERS.find(f => f.id === instance.featherId);
    if (!feather) return null;
    return {
      ...feather,
      instanceId: instance.instanceId,
      angle: instance.angle,
      x: instance.x,
      y: instance.y,
    };
  }).filter(Boolean);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: 'radial-gradient(ellipse at 50% 38%, #112240 0%, #020c1b 62%, #000000 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Pixelify Sans', cursive",
      }}
    >
      {/* Vignette */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 28%, rgba(0,0,0,0.72) 100%)',
        pointerEvents: 'none',
        zIndex: 1,
      }}/>

      {/* Title */}
      <div
        data-testid="wake-title"
        style={{
          position: 'absolute',
          top: '26px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '16px',
          letterSpacing: '0.6em',
          color: 'rgba(255, 215, 0, 0.28)',
          zIndex: 10,
          userSelect: 'none',
          fontFamily: "'Pixelify Sans', cursive",
        }}
      >
        W A K E
      </div>

      {/* Instruction text */}

      {/* Mute button */}
      <button
        data-testid="mute-button"
        onClick={handleMuteToggle}
        style={{
          position: 'absolute',
          top: '18px',
          right: '22px',
          background: 'rgba(8, 20, 40, 0.75)',
          border: '1px solid rgba(255,215,0,0.16)',
          borderRadius: '3px',
          padding: '7px 9px',
          color: 'rgba(255,215,0,0.42)',
          cursor: 'pointer',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isMuted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
      </button>

      {/* Feather count indicator */}
      <div
        data-testid="feather-count"
        style={{
          position: 'absolute',
          top: '18px',
          left: '22px',
          background: 'rgba(8, 20, 40, 0.75)',
          border: '1px solid rgba(255,215,0,0.16)',
          borderRadius: '3px',
          padding: '7px 12px',
          color: 'rgba(255,215,0,0.52)',
          fontSize: '14px',
          zIndex: 20,
          fontFamily: "'Caveat', cursive",
        }}
      >
        {placedFeathers.length} / {MAX_FEATHERS}
      </div>

      {/* ── WOODEN BOX ── */}
      <div
        data-testid="wooden-box"
        style={{
          position: 'relative',
          zIndex: 5,
          padding: '10px',
          background: `
            repeating-linear-gradient(93deg, transparent 0, transparent 52px, rgba(255,215,0,0.03) 52px, rgba(255,215,0,0.03) 53px),
            repeating-linear-gradient(177deg, transparent 0, transparent 38px, rgba(0,0,0,0.04) 38px, rgba(0,0,0,0.04) 39px),
            linear-gradient(150deg, #5a3a14 0%, #3a2208 26%, #4a3010 54%, #3a2208 78%, #5a3a14 100%)
          `,
          border: '3px solid #8a6b2f',
          borderBottom: '8px solid #3d2a10',
          borderRight: '5px solid #352408',
          boxShadow: '0 44px 110px rgba(0,0,0,0.92), 0 14px 42px rgba(0,0,0,0.72), inset 0 1px 0 rgba(255,255,255,0.05)',
          borderRadius: '3px',
          width: 'min(680px, 88vw)',
        }}
      >
        {/* Box velvet interior */}
        <div style={{
          background: '#050204',
          padding: '18px 16px 20px',
          borderRadius: '1px',
          boxShadow: 'inset 0 0 55px rgba(0,0,0,0.88)',
        }}>

          {/* Top row */}
          <div style={{ display: 'flex', gap: '7px', marginBottom: '16px' }}>
            {topFeathers.map(f => (
              <FeatherItem
                key={f.id}
                feather={f}
                isPlaced={false}
                canPlace={placedFeathers.length < MAX_FEATHERS && endingPhase === 0}
                onPlace={handlePlaceFeather}
                onWhisperStart={handleWhisperStart}
                onStartAudio={handleStartAudio}
                whisperActive={whisperActiveRef.current}
              />
            ))}
          </div>

          {/* Central gold mask */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '2px 0 16px' }}>
            <GoldMask
              placedFeathers={placedObjects}
              isGlowing={placedFeathers.length >= 4}
              onRemoveFeather={handleRemoveFeather}
              onUpdatePosition={handleUpdateFeatherPosition}
              onUpdateAngle={handleUpdateFeatherAngle}
              onComplete={handleCompleteMask}
              canComplete={placedFeathers.length > 0 && endingPhase === 0}
            />
          </div>

          {/* Instruction text — under the mask */}
          <div
            data-testid="instruction-text"
            style={{
              textAlign: 'center',
              fontSize: '18px',
              color: 'rgba(255, 215, 0, 0.45)',
              fontFamily: "'Caveat', cursive",
              letterSpacing: '0.04em',
              marginBottom: '8px',
              userSelect: 'none',
            }}
          >
            Choose your feathers. Complete the Mask.
          </div>

          {/* Bottom row */}
          <div style={{ display: 'flex', gap: '7px' }}>
            {bottomFeathers.map(f => (
              <FeatherItem
                key={f.id}
                feather={f}
                isPlaced={false}
                canPlace={placedFeathers.length < MAX_FEATHERS && endingPhase === 0}
                onPlace={handlePlaceFeather}
                onWhisperStart={handleWhisperStart}
                onStartAudio={handleStartAudio}
                whisperActive={whisperActiveRef.current}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── WHISPER OVERLAY ── */}
      {whisperData && (
        <div
          data-testid="whisper-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          <div style={{
            fontFamily: "'Caveat', cursive",
            fontSize: 'clamp(22px, 2.8vw, 36px)',
            color: '#e8f1ff',
            textAlign: 'center',
            letterSpacing: '0.03em',
            lineHeight: '2',
            textShadow: '0 0 32px rgba(255,215,0,0.22), 0 2px 10px rgba(0,0,0,0.92)',
            opacity: whisperData.phase === 'fading' ? 0 : 1,
            transition: 'opacity 1.6s ease-out',
            maxWidth: '80vw',
            padding: '0 20px',
          }}>
            {whisperData.visibleWords.map((word, i) => (
              <span
                key={`${word}-${i}`}
                style={{
                  display: 'inline-block',
                  marginRight: '0.3em',
                  opacity: 0,
                  animation: 'fadeInWord 0.9s ease-out forwards',
                }}
              >
                {word}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── ENDING OVERLAY ── */}
      {endingPhase >= 1 && (
        <div
          data-testid="ending-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(1, 7, 18, 0.84)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
          }}
        >
          <div style={{
            textAlign: 'center',
            padding: '0 44px',
            maxWidth: '500px',
          }}>
            <p
              data-testid="grams-text"
              style={{
                fontFamily: "'Caveat', cursive",
                fontSize: 'clamp(17px, 2.1vw, 26px)',
                color: '#ccdcf5',
                lineHeight: '1.9',
                textShadow: '0 0 28px rgba(255,215,0,0.18), 0 2px 8px rgba(0,0,0,0.85)',
                animation: 'gramsAppear 2.6s ease-out forwards',
                marginBottom: endingPhase >= 2 ? '50px' : 0,
              }}
            >
              Look what you've made here. Something very special indeed.
            </p>

            {endingPhase >= 2 && (
              <p
                data-testid="ready-text"
                style={{
                  fontFamily: "'Caveat', cursive",
                  fontSize: 'clamp(20px, 2.5vw, 32px)',
                  color: endingPhase >= 3 ? 'rgba(255, 215, 0, 1)' : 'rgba(255, 215, 0, 0.88)',
                  textShadow: endingPhase >= 3
                    ? '0 0 40px rgba(255,215,0,0.9), 0 0 80px rgba(255,215,0,0.6), 0 0 120px rgba(255,215,0,0.3)'
                    : '0 0 28px rgba(255,215,0,0.48)',
                  animation: endingPhase >= 3 ? 'readyGlow 1.5s ease-in-out infinite' : 'gramsAppear 2.6s ease-out forwards',
                  transition: 'color 1s, text-shadow 1s',
                }}
              >
                I'm ready.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── GOLD FLOOD ── */}
      {endingPhase >= 4 && (
        <div
          data-testid="gold-flood"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: 'radial-gradient(circle at 50% 50%, #ffd700, #c9a84c, #b8860b)',
            animation: 'goldFlood 3s ease-in forwards',
          }}
        />
      )}
    </div>
  );
}
